import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useAuth} from '../../services/use-auth.js';
import {Link, NavLink, useHistory, useLocation} from 'react-router-dom';
import './header.scss';
import logo from '../../assets/images/logo_en.svg';
import {useTranslation} from 'react-i18next';
import {applyLocale, normalizeLocale, saveUserLocale} from '../../services/locale';

function isMainMenuPath(pathname = '') {
    const path = pathname || '/';

    return path === '/' || path === '/main';
}

function isTestingMenuPath(pathname = '') {
    const path = pathname || '/';

    return path === '/testings'
        || path.startsWith('/testings/')
        || path === '/my-testings'
        || path === '/finished-testings';
}

function isCoursesMenuPath(pathname = '') {
    const path = pathname || '/';

    return path === '/courses'
        || path.startsWith('/courses/')
        || path === '/mycourses'
        || path === '/finished-courses'
        || path === '/course'
        || path.startsWith('/course/');
}

export default function Header() {
    const auth = useAuth();
    const location = useLocation();
    const history = useHistory();
    const {t, i18n} = useTranslation();
    const currentLocale = normalizeLocale(i18n.resolvedLanguage || i18n.language) || 'uk';
    const [searchQuery, setSearchQuery] = useState('');
    const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
    const userMenuRef = useRef(null);

    const handleLocaleChange = useCallback(async (nextLocale) => {
        const normalized = normalizeLocale(nextLocale) || 'uk';
        applyLocale(normalized);

        if (!auth?.user) {
            return;
        }

        try {
            const persistedLocale = await saveUserLocale(normalized);
            applyLocale(persistedLocale);
            if (auth?.setUser) {
                auth.setUser({
                    ...auth.user,
                    locale: persistedLocale,
                });
            }
        } catch (e) {
            // Keep UI language selected by user even if persistence fails.
        }
    }, [auth]);

    const closeUserMenu = useCallback(() => {
        setIsUserMenuOpen(false);
    }, []);

    const closeMobileMenu = useCallback(() => {
        setIsMobileMenuOpen(false);
    }, []);

    const closeMobileSearch = useCallback(() => {
        setIsMobileSearchOpen(false);
    }, []);

    useEffect(() => {
        return history.listen(() => {
            closeUserMenu();
            closeMobileMenu();
            closeMobileSearch();
        });
    }, [closeMobileMenu, closeMobileSearch, closeUserMenu, history]);

    useEffect(() => {
        if (location.pathname !== '/search') {
            return;
        }

        const params = new URLSearchParams(location.search || '');
        setSearchQuery(params.get('search') || '');
    }, [location.pathname, location.search]);

    useEffect(() => {
        function handleDocumentClick(event) {
            if (!userMenuRef.current || userMenuRef.current.contains(event.target)) {
                return;
            }

            closeUserMenu();
        }

        document.addEventListener('mousedown', handleDocumentClick);

        return () => {
            document.removeEventListener('mousedown', handleDocumentClick);
        };
    }, [closeUserMenu]);

    useEffect(() => {
        if (!isMobileMenuOpen && !isMobileSearchOpen) {
            return undefined;
        }

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        function handleEscape(event) {
            if (event.key !== 'Escape') {
                return;
            }

            closeMobileMenu();
            closeMobileSearch();
        }

        document.addEventListener('keydown', handleEscape);

        return () => {
            document.body.style.overflow = previousOverflow;
            document.removeEventListener('keydown', handleEscape);
        };
    }, [closeMobileMenu, closeMobileSearch, isMobileMenuOpen, isMobileSearchOpen]);

    const handleSearchSubmit = useCallback((event) => {
        event.preventDefault();
        const query = searchQuery.trim();
        const params = new URLSearchParams();

        if (query !== '') {
            params.set('search', query);
        }

        history.push({
            pathname: '/search',
            search: params.toString() ? `?${params.toString()}` : '',
        });
        closeMobileSearch();
        closeMobileMenu();
    }, [closeMobileMenu, closeMobileSearch, history, searchQuery]);

    const displayName = (auth.user?.firstName || auth.user?.lastName)
        ? `${auth.user.firstName || ''} ${auth.user.lastName || ''}`.trim()
        : t('menu.defaultUsername');
    const mainMenuPath = auth.user ? '/main' : '/';

    return (
        <header id="header" className="header" role="banner">
            <div className="main-container header-shell">
                <div className="header-utility">
                    <Link to="/main" className="am-brand" aria-label="Accemedin">
                        <img src={logo} alt="accemedin" />
                    </Link>

                    <form className="am-search" role="search" autoComplete="off" onSubmit={handleSearchSubmit}>
                        <label className="sr-only" htmlFor="header-search-input">{t('search.title', 'Пошук')}</label>
                        <span className="am-search-icon" aria-hidden="true" />
                        <input
                            id="header-search-input"
                            name="search"
                            type="search"
                            autoComplete="off"
                            value={searchQuery}
                            onChange={(event) => setSearchQuery(event.target.value)}
                            placeholder={t('search.inputPlaceholder', 'Пошук курсів, заходів, матеріалів…')}
                        />
                        <button type="submit">{t('search.submit', 'Знайти')}</button>
                    </form>

                    <div className="header-actions">
                        <button
                            type="button"
                            className="am-iconbtn am-searchbtn"
                            aria-label={t('search.title', 'Пошук')}
                            aria-controls="header-mobile-search"
                            aria-expanded={isMobileSearchOpen}
                            onClick={() => setIsMobileSearchOpen(true)}>
                            <svg viewBox="0 0 24 24" aria-hidden="true">
                                <path
                                    d="M10.5 3a7.5 7.5 0 105.02 13.08l3.2 3.2a1 1 0 001.41-1.41l-3.2-3.2A7.5 7.5 0 0010.5 3zm0 2a5.5 5.5 0 110 11 5.5 5.5 0 010-11z"
                                    fill="currentColor"
                                />
                            </svg>
                        </button>

                        <div className="locale-switcher" role="group" aria-label="Language">
                            <button
                                type="button"
                                className={`locale-switcher-button ${currentLocale === 'uk' ? 'active' : ''}`}
                                aria-pressed={currentLocale === 'uk'}
                                onClick={() => handleLocaleChange('uk')}>
                                UK
                            </button>
                            <span className="locale-switcher-divider">/</span>
                            <button
                                type="button"
                                className={`locale-switcher-button ${currentLocale === 'en' ? 'active' : ''}`}
                                aria-pressed={currentLocale === 'en'}
                                onClick={() => handleLocaleChange('en')}>
                                EN
                            </button>
                        </div>

                        {
                            !auth.user
                                ? (
                                    <div className="header-auth-links">
                                        <Link to={{pathname: '/account/login', state: {from: location}}} className="btn-registration">
                                            {t('buttons.login')}
                                        </Link>
                                    </div>
                                )
                                : (
                                    <div className={`header-user-menu ${isUserMenuOpen ? 'is-open' : ''}`} ref={userMenuRef}>
                                        <button
                                            type="button"
                                            className="am-user"
                                            aria-haspopup="menu"
                                            aria-expanded={isUserMenuOpen}
                                            onClick={() => setIsUserMenuOpen((current) => !current)}>
                                            <img src={auth.user.avatarUrl ?? '/img/no-photo.svg'} alt="" className="am-avatar" />
                                            <span className="am-user-name">{displayName}</span>
                                            <span className="am-user-chevron" aria-hidden="true" />
                                        </button>

                                        <ul className="profile-menu" role="menu">
                                            <li role="none">
                                                <Link role="menuitem" to="/profile" onClick={closeUserMenu}>{t('menu.summary')}</Link>
                                            </li>
                                            <li role="none">
                                                <NavLink role="menuitem" to="/portfolio" onClick={closeUserMenu}>{t('menu.portfolio')}</NavLink>
                                            </li>
                                            <li role="none">
                                                <Link role="menuitem" to="/main/certificates" onClick={closeUserMenu}>{t('menu.certificates')}</Link>
                                            </li>
                                            <li role="none">
                                                <Link role="menuitem" to="/promoCodes" onClick={closeUserMenu}>{t('menu.promocodes')}</Link>
                                            </li>
                                            <li role="none">
                                                <button
                                                    type="button"
                                                    role="menuitem"
                                                    className="profile-menu-action"
                                                    onClick={() => {
                                                        closeUserMenu();
                                                        auth.signOut();
                                                    }}>
                                                    {t('buttons.logout')}
                                                </button>
                                            </li>
                                        </ul>
                                    </div>
                                )
                        }

                        <button
                            type="button"
                            className={`am-burger ${isMobileMenuOpen ? 'is-open' : ''}`}
                            aria-label={t('menu.navigationAria', 'Головне меню')}
                            aria-controls="header-mobile-menu"
                            aria-expanded={isMobileMenuOpen}
                            onClick={() => setIsMobileMenuOpen((current) => !current)}>
                            <span />
                            <span />
                            <span />
                        </button>
                    </div>
                </div>

                <nav className="header-nav-row" id="nav" aria-label={t('menu.navigationAria', 'Головне меню')}>
                    <ul className="main-menu" id="mainmenu">
                        <li>
                            <NavLink
                                activeClassName="current-page"
                                to={mainMenuPath}
                                isActive={(match, currentLocation) => isMainMenuPath(currentLocation?.pathname)}>
                                {t('menu.main')}
                            </NavLink>
                        </li>
                        <li>
                            <NavLink
                                activeClassName="current-page"
                                to="/testings"
                                isActive={(match, currentLocation) => isTestingMenuPath(currentLocation?.pathname)}>
                                {t('menu.testings')}
                            </NavLink>
                        </li>
                        <li>
                            <NavLink
                                activeClassName="current-page"
                                to="/courses"
                                isActive={(match, currentLocation) => isCoursesMenuPath(currentLocation?.pathname)}>
                                {t('menu.courses')}
                            </NavLink>
                        </li>
                        <li>
                            <NavLink activeClassName="current-page" to="/conferences">{t('menu.conferences')}</NavLink>
                        </li>
                        <li>
                            <NavLink activeClassName="current-page" to="/library">{t('menu.library')}</NavLink>
                        </li>
                        <li>
                            <NavLink activeClassName="current-page" to="/surveys">{t('menu.surveys')}</NavLink>
                        </li>
                        <li>
                            <NavLink activeClassName="current-page" to="/organizations">{t('menu.organizations')}</NavLink>
                        </li>
                    </ul>
                </nav>

                <div
                    className={`am-backdrop ${isMobileMenuOpen ? 'is-open' : ''}`}
                    aria-hidden="true"
                    onClick={closeMobileMenu}
                />

                <aside
                    id="header-mobile-menu"
                    className={`am-sheet ${isMobileMenuOpen ? 'is-open' : ''}`}
                    aria-label={t('menu.navigationAria', 'Головне меню')}>
                    <div className="am-sheet__head">
                        <div className="am-sheet__brand">
                            <img src={logo} alt="accemedin" />
                        </div>
                        <button
                            type="button"
                            className="am-iconbtn"
                            aria-label={t('buttons.close', 'Закрити')}
                            onClick={closeMobileMenu}>
                            <svg viewBox="0 0 24 24" aria-hidden="true">
                                <path
                                    d="M6 6l12 12M18 6L6 18"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                />
                            </svg>
                        </button>
                    </div>
                    <div className="am-sheet__body">
                        <nav className="am-sheet__nav" aria-label={t('menu.navigationAria', 'Головне меню')}>
                            <NavLink
                                className="am-sheet__link"
                                activeClassName="is-active"
                                to={mainMenuPath}
                                isActive={(match, currentLocation) => isMainMenuPath(currentLocation?.pathname)}
                                onClick={closeMobileMenu}>
                                {t('menu.main')}
                            </NavLink>
                            <NavLink
                                className="am-sheet__link"
                                activeClassName="is-active"
                                to="/testings"
                                isActive={(match, currentLocation) => isTestingMenuPath(currentLocation?.pathname)}
                                onClick={closeMobileMenu}>
                                {t('menu.testings')}
                            </NavLink>
                            <NavLink
                                className="am-sheet__link"
                                activeClassName="is-active"
                                to="/courses"
                                isActive={(match, currentLocation) => isCoursesMenuPath(currentLocation?.pathname)}
                                onClick={closeMobileMenu}>
                                {t('menu.courses')}
                            </NavLink>
                            <NavLink className="am-sheet__link" activeClassName="is-active" to="/conferences" onClick={closeMobileMenu}>
                                {t('menu.conferences')}
                            </NavLink>
                            <NavLink className="am-sheet__link" activeClassName="is-active" to="/library" onClick={closeMobileMenu}>
                                {t('menu.library')}
                            </NavLink>
                            <NavLink className="am-sheet__link" activeClassName="is-active" to="/surveys" onClick={closeMobileMenu}>
                                {t('menu.surveys')}
                            </NavLink>
                            <NavLink className="am-sheet__link" activeClassName="is-active" to="/organizations" onClick={closeMobileMenu}>
                                {t('menu.organizations')}
                            </NavLink>
                        </nav>

                        <div className="am-sheet__divider" />

                        <div className="am-sheet__row">
                            <span className="am-sheet__label">Language</span>
                            <div className="locale-switcher mobile" role="group" aria-label="Language">
                                <button
                                    type="button"
                                    className={`locale-switcher-button ${currentLocale === 'uk' ? 'active' : ''}`}
                                    aria-pressed={currentLocale === 'uk'}
                                    onClick={() => handleLocaleChange('uk')}>
                                    UK
                                </button>
                                <span className="locale-switcher-divider">/</span>
                                <button
                                    type="button"
                                    className={`locale-switcher-button ${currentLocale === 'en' ? 'active' : ''}`}
                                    aria-pressed={currentLocale === 'en'}
                                    onClick={() => handleLocaleChange('en')}>
                                    EN
                                </button>
                            </div>
                        </div>

                        {!auth.user && (
                            <div className="am-sheet__auth">
                                <Link
                                    className="am-sheet__link"
                                    to={{pathname: '/account/login', state: {from: location}}}
                                    onClick={closeMobileMenu}>
                                    {t('buttons.login')}
                                </Link>
                            </div>
                        )}

                        {!!auth.user && (
                            <div className="am-sheet__profile">
                                <img src={auth.user.avatarUrl ?? '/img/no-photo.svg'} alt="" className="am-avatar am-avatar--lg" />
                                <div className="am-sheet__profile-content">
                                    <div className="am-sheet__name">{displayName}</div>
                                    <Link className="am-sheet__profilelink" to="/profile" onClick={closeMobileMenu}>
                                        {t('menu.summary')}
                                    </Link>
                                    <button
                                        type="button"
                                        className="am-sheet__logout"
                                        onClick={() => {
                                            closeMobileMenu();
                                            auth.signOut();
                                        }}>
                                        {t('buttons.logout')}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </aside>

                {isMobileSearchOpen && (
                    <div id="header-mobile-search" className="am-searchov" role="dialog" aria-modal="true" aria-label={t('search.title', 'Пошук')}>
                        <div className="am-searchov__panel">
                            <form className="am-search am-search--mobile" role="search" autoComplete="off" onSubmit={handleSearchSubmit}>
                                <label className="sr-only" htmlFor="header-mobile-search-input">{t('search.title', 'Пошук')}</label>
                                <span className="am-search-icon" aria-hidden="true" />
                                <input
                                    id="header-mobile-search-input"
                                    name="search"
                                    type="search"
                                    autoComplete="off"
                                    value={searchQuery}
                                    onChange={(event) => setSearchQuery(event.target.value)}
                                    placeholder={t('search.inputPlaceholder', 'Пошук курсів, заходів, матеріалів…')}
                                />
                                <button type="submit">{t('search.submit', 'Знайти')}</button>
                                <button type="button" className="am-searchov__cancel" onClick={closeMobileSearch}>
                                    {t('buttons.cancel', 'Скасувати')}
                                </button>
                            </form>
                        </div>
                        <div className="am-searchov__backdrop" aria-hidden="true" onClick={closeMobileSearch} />
                    </div>
                )}
            </div>
        </header>
    );
}
